import type { DataSource } from './source';
import type { POI, Sentiero, Itinerario, POIType, SentieroDifficulty } from '../domain/types';

export class StaticDataSource implements DataSource {
  private overlaysBase = '/overlays';
  private cache: { pois?: POI[]; sentieri?: Sentiero[]; itinerari?: Itinerario[] } = {};

  async getPOIs(): Promise<POI[]> {
    if (this.cache.pois) return this.cache.pois;
    const fc = await this.fetchGeoJSON('poi.geojson');
    this.cache.pois = fc.features.map((f) => this.featureToPOI(f));
    return this.cache.pois;
  }

  async getPOIById(id: string): Promise<POI | null> {
    return (await this.getPOIs()).find((p) => p.id === id) ?? null;
  }

  async getSentieri(): Promise<Sentiero[]> {
    if (this.cache.sentieri) return this.cache.sentieri;
    const fc = await this.fetchGeoJSON('sentieri.geojson');
    this.cache.sentieri = fc.features.map((f) => this.featureToSentiero(f));
    return this.cache.sentieri;
  }

  async getItinerari(): Promise<Itinerario[]> {
    if (this.cache.itinerari) return this.cache.itinerari;
    const fc = await this.fetchGeoJSON('itinerari.geojson');
    this.cache.itinerari = fc.features.map((f) => this.featureToItinerario(f));
    return this.cache.itinerari;
  }

  private async fetchGeoJSON(filename: string): Promise<GeoJSON.FeatureCollection> {
    const res = await fetch(`${this.overlaysBase}/${filename}`);
    if (!res.ok) {
      throw new Error(`Failed to load ${filename}: HTTP ${res.status}`);
    }
    return res.json() as Promise<GeoJSON.FeatureCollection>;
  }

  private featureToPOI(f: GeoJSON.Feature): POI {
    const p = (f.properties ?? {}) as Record<string, unknown>;
    if (f.geometry.type !== 'Point') {
      throw new Error(`POI ${String(p.id)}: geometry must be Point, got ${f.geometry.type}`);
    }
    const [lng, lat] = f.geometry.coordinates as [number, number];
    return {
      id: String(p.id),
      name: String(p.name),
      type: (p.type as POIType) ?? 'altro',
      description: p.description as string | undefined,
      coords: { lng, lat },
      tags: (p.tags as string[] | undefined) ?? [],
    };
  }

  private featureToSentiero(f: GeoJSON.Feature): Sentiero {
    const p = (f.properties ?? {}) as Record<string, unknown>;
    if (f.geometry.type !== 'LineString') {
      throw new Error(`Sentiero ${String(p.id)}: geometry must be LineString, got ${f.geometry.type}`);
    }
    return {
      id: String(p.id),
      name: String(p.name),
      caiNumber: p.caiNumber as string | undefined,
      difficulty: p.difficulty as SentieroDifficulty | undefined,
      coords: f.geometry.coordinates as [number, number][],
    };
  }

  private featureToItinerario(f: GeoJSON.Feature): Itinerario {
    const p = (f.properties ?? {}) as Record<string, unknown>;
    return {
      id: String(p.id),
      name: String(p.name),
      description: p.description as string | undefined,
      poiIds: (p.poiIds as string[] | undefined) ?? [],
      sentieroIds: (p.sentieroIds as string[] | undefined) ?? [],
    };
  }
}
